import React, { useCallback, useEffect, useRef, useState } from 'react';
import { HubConnection, HubConnectionBuilder, HubConnectionState } from '@microsoft/signalr';

import { TOKENS } from 'constants/index';
import { useApiRequests, useSignalrConnection } from 'hooks';
import { useAppSelector } from 'store';
import { selectUser } from 'store/user';
import { ConsentSection, FormState, SignatureState, SubmitReceipt } from 'components/Teleconsent/types';
import { registerDynamicForms } from 'components/Teleconsent/forms/dynamicRegistry';
import { TeleconsentContext, TeleconsentMode, CursorPosition, HostScrollTarget, ClientViewport } from './context';

const HUB_URL = `${process.env['REACT_APP_HIPAA_LINK_API_URI']}teleconsentHub`;
const CURSOR_THROTTLE_MS = 30;
const SCROLL_THROTTLE_MS = 30;

const getToken = (): string => {
  try {
    const tokensJson = window.localStorage.getItem(TOKENS);
    const parsed = tokensJson ? JSON.parse(tokensJson) : null;
    return parsed?.token ?? '';
  } catch {
    return '';
  }
};

type Props = { children?: React.ReactNode; active?: boolean };

export function TeleconsentProvider({ children, active = true }: Props) {
  const { startTeleconsentSession, submitTeleconsentForm, getCustomConsentForms, getCustomConsentForm } =
    useApiRequests();
  const { signalrConnectionId } = useSignalrConnection();
  const currentUser = useAppSelector(selectUser);
  const hubRef = useRef<HubConnection | null>(null);
  const cursorThrottleRef = useRef<number>(0);
  const scrollThrottleRef = useRef<number>(0);
  const signalrConnectionIdRef = useRef<string | null>(null);

  const isOpenRef = useRef(false);
  const isHostRef = useRef(false);
  const modeRef = useRef<TeleconsentMode>('selfpaced');

  const [goBackSignal, setGoBackSignal] = useState(0);

  const [isOpen, setIsOpen] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [formKey, setFormKey] = useState<string | null>(null);
  const [isHost, setIsHost] = useState(false);
  const [mode, setModeState] = useState<TeleconsentMode>('selfpaced');
  const [fields, setFields] = useState<FormState>({});
  const [signature, setSignatureState] = useState<SignatureState>({ kind: 'draw', dataUrl: '', text: '' });
  const [receipt, setReceipt] = useState<SubmitReceipt | null>(null);
  const [cursors, setCursors] = useState<CursorPosition[]>([]);
  const [highlightedBlockId, setHighlightedBlockId] = useState<string | null>(null);
  const [clientDownloaded, setClientDownloaded] = useState(false);
  const [hostScrollTarget, setHostScrollTarget] = useState<HostScrollTarget | null>(null);
  const [clientViewport, setClientViewport] = useState<ClientViewport | null>(null);
  const [participantName, setParticipantName] = useState<string | null>(null);
  const [providerName, setProviderName] = useState<string | null>(null);
  const [clientLabel, setClientLabel] = useState<string>('Patient');
  const [clientEndedSession, setClientEndedSession] = useState(false);
  const clientEndedSessionRef = useRef(false);

  // Load custom consent forms from DB and register full definitions in the dynamic registry.
  // Guard on currentUser so this never fires for unauthenticated patients (Bearer undefined → 401).
  useEffect(() => {
    if (!currentUser) return;
    let cancelled = false;
    getCustomConsentForms()
      .then((summaries) => {
        const enabled = summaries.filter((s) => s.isEnabled);
        return Promise.all(enabled.map((s) => getCustomConsentForm(s.id)));
      })
      .then((details) => {
        if (cancelled) return;
        registerDynamicForms(
          details.map((d) => {
            let parsed: { sections?: unknown[]; requiredFields?: string[] } = {};
            try {
              parsed = JSON.parse(d.definition) as typeof parsed;
            } catch {
              /* ignore malformed definition */
            }
            return {
              formKey: d.formKey,
              title: d.title,
              subtitle: d.subtitle,
              intro: d.intro,
              pdfTitle: d.pdfTitle,
              sections: (parsed.sections ?? []) as ConsentSection[],
              requiredFields: parsed.requiredFields ?? [],
            };
          }),
        );
      })
      .catch((err) => {
        console.error('[TeleconsentProvider] failed to load custom forms:', err);
      });
    return () => {
      cancelled = true;
    };
  }, [currentUser?.id]); // eslint-disable-line

  // Keep refs in sync for use inside hub closures
  useEffect(() => {
    signalrConnectionIdRef.current = signalrConnectionId ?? null;
  }, [signalrConnectionId]);

  useEffect(() => {
    isOpenRef.current = isOpen;
  }, [isOpen]);

  useEffect(() => {
    isHostRef.current = isHost;
  }, [isHost]);

  useEffect(() => {
    modeRef.current = mode;
  }, [mode]);

  const registerClient = useCallback((c: HubConnection) => {
    const id = signalrConnectionIdRef.current;
    if (id) {
      c.invoke('RegisterClient', id).catch((err: Error) => {
        if (!err?.message?.includes('SIGNALR_TELECONSENT_FEATURE_NOT_ENABLED')) {
          console.error(err);
        }
      });
    } else {
      console.warn('[TeleconsentHub] RegisterClient skipped — signalrConnectionId not yet available');
    }
  }, []);

  const buildAndAttachHub = useCallback((): HubConnection => {
    const token = getToken();
    const builder = new HubConnectionBuilder().withAutomaticReconnect();
    // Guests have no JWT — connect without auth so the backend can allow anonymous
    const conn = (
      token ? builder.withUrl(HUB_URL, { accessTokenFactory: getToken }) : builder.withUrl(HUB_URL)
    ).build();

    hubRef.current = conn;

    conn.onreconnected(() => registerClient(conn));

    conn.onclose(() => {
      console.warn('[TeleconsentHub] connection closed');
      if (isOpenRef.current && !isHostRef.current) {
        resetState();
      }
    });

    return conn;
  }, [registerClient]);

  // Connect to the Teleconsent hub on mount (HIPAA Link only)
  useEffect(() => {
    if (!active) return;
    const conn = buildAndAttachHub();

    conn.on(
      'teleconsentOpened',
      (payload: {
        sessionId: string;
        formType: string;
        mode: TeleconsentMode;
        hostName?: string;
        providerPrefill?: Record<string, string>;
        providerName?: string;
        clientLabel?: string;
        customFormDefinition?: string;
      }) => {
        // Client must join the session group to receive all subsequent hub broadcasts
        // (scroll, mode change, field sync, etc. all target OthersInGroup(sessionId))
        const broadcastViewport = (sid: string) => {
          // Mirrors the modal CSS: width: min(720px, 100%-64px scrim); height: min(86vh, 880px)
          // Mobile breakpoint (≤640px): modal is full-screen, no scrim padding.
          const isMobile = window.innerWidth <= 640;
          const w = isMobile ? window.innerWidth : Math.min(720, window.innerWidth - 64);
          const h = isMobile ? window.innerHeight : Math.min(Math.round(window.innerHeight * 0.86), 880);
          const vp = `${w},${h}`;
          conn.invoke('UpdateTeleconsentField', sid, '__clientViewport', vp).catch(console.error);
        };

        // Broadcast only after JoinSession resolves so the server has added this
        // connection to the group before the field update is routed.
        conn
          .invoke('JoinSession', payload.sessionId)
          .then(() => broadcastViewport(payload.sessionId))
          .catch(console.error);

        const onResize = () => broadcastViewport(payload.sessionId);
        window.addEventListener('resize', onResize);
        conn.onclose(() => window.removeEventListener('resize', onResize));

        // If a custom form definition was included, register it so the renderer can find it
        if (payload.customFormDefinition) {
          try {
            const cd = JSON.parse(payload.customFormDefinition) as {
              formKey: string;
              title: string;
              subtitle?: string;
              intro?: string;
              pdfTitle: string;
              definition: string;
            };
            const inner = JSON.parse(cd.definition) as { sections?: ConsentSection[]; requiredFields?: string[] };
            registerDynamicForms([
              {
                formKey: cd.formKey,
                title: cd.title,
                subtitle: cd.subtitle,
                intro: cd.intro,
                pdfTitle: cd.pdfTitle,
                sections: inner.sections ?? [],
                requiredFields: inner.requiredFields ?? [],
              },
            ]);
          } catch {
            /* malformed — fall through; form won't render but won't crash */
          }
        }

        setSessionId(payload.sessionId);
        setFormKey(payload.formType);
        setIsHost(false);
        setModeState(payload.mode);
        if (payload.hostName) setParticipantName(payload.hostName);
        if (payload.providerName) setProviderName(payload.providerName);
        if (payload.clientLabel) setClientLabel(payload.clientLabel);
        if (payload.providerPrefill) {
          setFields((prev) => ({ ...prev, ...payload.providerPrefill }));
        }
        setIsOpen(true);
      },
    );

    conn.on(
      'teleconsentFieldChanged',
      (payload: { fieldId: string; value: string | boolean; connectionId: string }) => {
        if (payload.connectionId === conn.connectionId) return;
        if (payload.fieldId === '__goBack' && !isHostRef.current) {
          setGoBackSignal((n) => n + 1);
          return;
        }
        if (payload.fieldId === '__clientClosed' && isHostRef.current) {
          clientEndedSessionRef.current = true;
          setClientEndedSession(true);
          return;
        }
        if (payload.fieldId === '__clientViewport') {
          const raw = String(payload.value);
          const [w, h] = raw.split(',').map(Number);
          if (w && h) setClientViewport({ width: w, height: h });
          return;
        }
        setFields((prev) => ({ ...prev, [payload.fieldId]: payload.value }));
        if (payload.fieldId === 'signature' && typeof payload.value === 'string') {
          const v = payload.value as string;
          const kind = v.startsWith('data:image/') ? 'draw' : 'type';
          setSignatureState({ kind, dataUrl: kind === 'draw' ? v : '', text: kind === 'type' ? v : '' });
        }
      },
    );

    conn.on('teleconsentModeChanged', (payload: { sessionId: string; mode: TeleconsentMode }) => {
      setModeState(payload.mode);
    });

    conn.on('teleconsentHostScrolled', (payload: { sectionId: string; sectionFraction: number }) => {
      setHostScrollTarget({ sectionId: payload.sectionId, sectionFraction: payload.sectionFraction });
    });

    conn.on('teleconsentBlockHighlighted', (payload: { sessionId: string; blockId: string | null }) => {
      setHighlightedBlockId(payload.blockId);
    });

    conn.on('teleconsentCursorMoved', (payload: CursorPosition) => {
      if (payload.connectionId === conn.connectionId) return;
      setCursors((prev) => {
        const others = prev.filter((c) => c.connectionId !== payload.connectionId);
        return [...others, payload];
      });
    });

    conn.on('teleconsentSubmitted', (payload: { submittedAt: string; clientIp: string }) => {
      setReceipt({ submittedAt: payload.submittedAt, clientIp: payload.clientIp });
    });

    conn.on('teleconsentClientDownloaded', () => {
      setClientDownloaded(true);
    });

    conn.on('teleconsentClosed', () => {
      if (isHostRef.current && clientEndedSessionRef.current) return;
      resetState();
    });

    conn
      .start()
      .then(() => {
        registerClient(conn);
      })
      .catch((err) => {
        console.error('[TeleconsentHub] initial connection failed:', err);
      });

    return () => {
      conn.stop();
    };
  }, []);

  // When signalrConnectionId arrives (always async for guests):
  //  - If hub is Connected → call RegisterClient (happy path for providers)
  //  - If hub is Disconnected → it likely failed auth; reconnect now that we have the ID
  useEffect(() => {
    if (!active || !signalrConnectionId) return;
    const hub = hubRef.current;
    if (!hub) return;

    if (hub.state === HubConnectionState.Connected) {
      registerClient(hub);
    } else if (hub.state === HubConnectionState.Disconnected) {
      hub
        .start()
        .then(() => {
          registerClient(hub);
        })
        .catch((err) => {
          console.error('[TeleconsentHub] retry connection failed:', err);
        });
    }
  }, [signalrConnectionId, registerClient]);

  // When the host closes the tab/window, broadcast session close so the client dismisses
  useEffect(() => {
    const handleUnload = () => {
      const hub = hubRef.current;
      const sid = sessionId;
      if (hub?.state === HubConnectionState.Connected && sid && isHost) {
        hub.invoke('CloseTeleconsent', sid).catch(console.error);
      }
    };
    window.addEventListener('beforeunload', handleUnload);
    return () => window.removeEventListener('beforeunload', handleUnload);
  }, [sessionId, isHost]);

  // Re-broadcast the client's viewport once the form is confirmed open.
  // This catches any race where the initial broadcast (after JoinSession) was
  // dropped because the host hadn't yet processed the session.
  useEffect(() => {
    if (!isOpen || isHost || !sessionId) return;
    const hub = hubRef.current;
    if (!hub || hub.state !== HubConnectionState.Connected) return;
    const isMobile = window.innerWidth <= 640;
    const w = isMobile ? window.innerWidth : Math.min(720, window.innerWidth - 64);
    const h = isMobile ? window.innerHeight : Math.min(Math.round(window.innerHeight * 0.86), 880);
    hub.invoke('UpdateTeleconsentField', sessionId, '__clientViewport', `${w},${h}`).catch(console.error);
  }, [isOpen, isHost, sessionId]);

  const resetState = () => {
    setIsOpen(false);
    setSessionId(null);
    setFormKey(null);
    setIsHost(false);
    setModeState('selfpaced');
    setFields({});
    setSignatureState({ kind: 'draw', dataUrl: '', text: '' });
    setReceipt(null);
    setCursors([]);
    setHighlightedBlockId(null);
    setClientDownloaded(false);
    setHostScrollTarget(null);
    setClientViewport(null);
    setParticipantName(null);
    setProviderName(null);
    setClientLabel('Patient');
    clientEndedSessionRef.current = false;
    setClientEndedSession(false);
  };

  const startSession = useCallback(
    async (
      fKey: string,
      participantIdentity: string | null,
      providerPrefill?: Record<string, string>,
      participantDisplayName?: string,
      pName?: string,
      cLabel?: string,
    ) => {
      const enrichedPrefill: Record<string, string> = { ...providerPrefill };
      if (pName) enrichedPrefill['__providerName'] = pName;
      const data = await startTeleconsentSession({
        formKey: fKey,
        participantIdentity,
        providerPrefill: enrichedPrefill,
        providerName: pName,
        clientLabel: cLabel,
      });
      const hub = hubRef.current;
      if (hub?.state === HubConnectionState.Connected) {
        await hub.invoke('JoinSession', data.sessionId).catch(console.error);
      }
      setSessionId(data.sessionId);
      setFormKey(fKey);
      setIsHost(true);
      if (participantDisplayName) setParticipantName(participantDisplayName);
      if (pName) setProviderName(pName);
      if (cLabel) setClientLabel(cLabel);
      // Populate host's form fields with the same prefill values sent to the client
      if (providerPrefill && Object.keys(providerPrefill).length > 0) {
        setFields((prev) => ({ ...prev, ...providerPrefill }));
      }
      setIsOpen(true);
    },
    [startTeleconsentSession],
  );

  const submitForm = useCallback(async (): Promise<SubmitReceipt> => {
    const data = await submitTeleconsentForm(sessionId!);
    setReceipt(data);
    return data;
  }, [sessionId, submitTeleconsentForm]);

  const endSession = useCallback(() => {
    const hub = hubRef.current;
    if (hub?.state === HubConnectionState.Connected && sessionId) {
      hub.invoke('CloseTeleconsent', sessionId).catch(console.error);
    }
    resetState();
  }, [sessionId]);

  const updateField = useCallback(
    (fieldId: string, value: string | boolean) => {
      setFields((prev) => ({ ...prev, [fieldId]: value }));
      if (fieldId === 'signature' && typeof value === 'string') {
        const kind = value.startsWith('data:image/') ? 'draw' : 'type';
        setSignatureState({ kind, dataUrl: kind === 'draw' ? value : '', text: kind === 'type' ? value : '' });
      }
      const hub = hubRef.current;
      if (hub?.state === HubConnectionState.Connected && sessionId) {
        // Signature PNGs can be 200–500 KB and would exceed SignalR's message limit,
        // killing the WebSocket connection. Send a small boolean marker instead so the
        // host's progress bar updates without blowing up the channel. The actual image
        // is stored locally and included in the PDF on submission.
        const hubValue =
          fieldId === 'signature' && typeof value === 'string' && value.startsWith('data:') ? true : value;
        hub.invoke('UpdateTeleconsentField', sessionId, fieldId, hubValue).catch(console.error);
      }
    },
    [sessionId],
  );

  const updateSignature = useCallback(
    (dataUrl: string, text: string, kind: 'draw' | 'type') => {
      setSignatureState({ kind, dataUrl, text });
      const hub = hubRef.current;
      if (hub?.state === HubConnectionState.Connected && sessionId) {
        hub.invoke('UpdateTeleconsentField', sessionId, 'signature', dataUrl || text).catch(console.error);
      }
    },
    [sessionId],
  );

  const moveCursor = useCallback(
    (x: number, y: number) => {
      const now = Date.now();
      if (now - cursorThrottleRef.current < CURSOR_THROTTLE_MS) return;
      cursorThrottleRef.current = now;
      const hub = hubRef.current;
      if (hub?.state === HubConnectionState.Connected && sessionId) {
        hub.invoke('MoveTeleconsentCursor', sessionId, x, y).catch(console.error);
      }
    },
    [sessionId],
  );

  const focusPosition = useCallback(
    (sectionId: string, sectionFraction: number) => {
      const hub = hubRef.current;
      if (hub?.state === HubConnectionState.Connected && sessionId && isHost) {
        hub.invoke('FocusTeleconsentPosition', sessionId, sectionId, sectionFraction).catch(console.error);
      }
    },
    [sessionId, isHost],
  );

  const scrollPosition = useCallback(
    (sectionId: string, sectionFraction: number) => {
      const now = Date.now();
      if (now - scrollThrottleRef.current < SCROLL_THROTTLE_MS) return;
      scrollThrottleRef.current = now;
      const hub = hubRef.current;
      const currentMode = modeRef.current;
      const currentIsHost = isHostRef.current;
      const shouldEmit = (currentMode === 'guided' && currentIsHost) || (currentMode === 'follow' && !currentIsHost);
      if (hub?.state === HubConnectionState.Connected && sessionId && shouldEmit) {
        hub.invoke('ScrollTeleconsent', sessionId, sectionId, sectionFraction).catch(console.error);
      }
    },
    [sessionId],
  );

  const highlightBlock = useCallback(
    (blockId: string | null) => {
      setHighlightedBlockId(blockId);
      const hub = hubRef.current;
      if (hub?.state === HubConnectionState.Connected && sessionId && isHost) {
        hub.invoke('HighlightTeleconsentBlock', sessionId, blockId).catch(console.error);
      }
    },
    [sessionId, isHost],
  );

  const setMode = useCallback(
    (newMode: TeleconsentMode) => {
      setModeState(newMode);
      const hub = hubRef.current;
      if (hub?.state === HubConnectionState.Connected && sessionId && isHost) {
        hub.invoke('SetTeleconsentMode', sessionId, newMode).catch(console.error);
      }
    },
    [sessionId, isHost],
  );

  const notifyDownloaded = useCallback(() => {
    const hub = hubRef.current;
    if (hub?.state === HubConnectionState.Connected && sessionId) {
      hub.invoke('NotifyTeleconsentDownloaded', sessionId).catch(console.error);
    }
  }, [sessionId]);

  const notifyGoBack = useCallback(() => {
    const hub = hubRef.current;
    if (hub?.state === HubConnectionState.Connected && sessionId && isHost) {
      hub.invoke('UpdateTeleconsentField', sessionId, '__goBack', true).catch(console.error);
    }
  }, [sessionId, isHost]);

  const notifyClientClosed = useCallback(() => {
    const hub = hubRef.current;
    if (hub?.state === HubConnectionState.Connected && sessionId) {
      hub.invoke('UpdateTeleconsentField', sessionId, '__clientClosed', true).catch(console.error);
    }
  }, [sessionId]);

  const dismissClientEndedSession = useCallback(() => {
    resetState();
  }, []);

  const applySignature = useCallback(
    (payload: string) => {
      const isDraw = payload.startsWith('data:');
      if (isDraw) {
        setSignatureState({ kind: 'draw', dataUrl: payload, text: '' });
        setFields((prev) => ({ ...prev, signature: payload }));
      } else if (payload) {
        setSignatureState({ kind: 'type', dataUrl: '', text: payload });
        setFields((prev) => ({ ...prev, signature: payload }));
      }
      const hub = hubRef.current;
      if (hub?.state === HubConnectionState.Connected && sessionId) {
        // Draw: send actual JPEG dataUrl (already compressed to ~50 KB)
        // Type: send the typed text directly
        hub.invoke('UpdateTeleconsentField', sessionId, 'signature', payload || true).catch(console.error);
      }
    },
    [sessionId],
  );

  return (
    <TeleconsentContext.Provider
      value={{
        isOpen,
        sessionId,
        formKey,
        isHost,
        mode,
        fields,
        signature,
        receipt,
        cursors,
        highlightedBlockId,
        clientDownloaded,
        hostScrollTarget,
        clientViewport,
        participantName,
        providerName,
        clientLabel,
        startSession,
        submitForm,
        endSession,
        updateField,
        updateSignature,
        moveCursor,
        focusPosition,
        scrollPosition,
        highlightBlock,
        setMode,
        goBackSignal,
        clientEndedSession,
        notifyDownloaded,
        applySignature,
        notifyGoBack,
        notifyClientClosed,
        dismissClientEndedSession,
      }}
    >
      {children}
    </TeleconsentContext.Provider>
  );
}
